import {
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  StarFilled,
  StarOutlined,
} from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Empty,
  Popconfirm,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import React, { useState } from 'react';
import { DATE_FORMATS, GENDERS } from '../../../../../../common/constants';
import { formatDate } from '../../../../../../common/utils';
import {
  CREATE_ULTRA_PLAN_CHILD_PROFILE,
  UPDATE_ULTRA_PLAN_CHILD_PROFILE,
} from '../../../../graphql/Mutations';
import useDefaultChildSwitch from '../../../../hooks/useDefaultChildSwitch';
import {
  getChildAgeLabel,
  getFullName,
  getInitials,
} from '../../../../ultraPlanUtils';
import ChildProfileModal from './ChildProfileModal';

const { Text } = Typography;

const getGenderLabel = (gender) =>
  GENDERS?.find((option) => option?.value === gender)?.label || null;

const ChildProfilesSection = ({
  userSubscriptionId,
  childProfiles,
  loading,
  hasError,
  onRetry,
  onChanged,
}) => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    childProfile: null,
  });

  const [
    createChildProfile,
    { loading: isCreating },
  ] = useMutation(CREATE_ULTRA_PLAN_CHILD_PROFILE, { onError() {} });
  const [
    updateChildProfile,
    { loading: isUpdating },
  ] = useMutation(UPDATE_ULTRA_PLAN_CHILD_PROFILE, { onError() {} });
  const { switchDefaultChild, switchingChildId } = useDefaultChildSwitch({
    userSubscriptionId,
    onSwitched: onChanged,
  });

  const hasDefaultChild = childProfiles?.some(
    (child) => child?.isDefault && child?.isActive,
  );
  const openAddModal = () =>
    setModalState({ isOpen: true, childProfile: null });
  const closeModal = () => setModalState({ isOpen: false, childProfile: null });

  const handleSubmit = async (values) => {
    const editingChild = modalState?.childProfile;
    const response = editingChild?.id
      ? await updateChildProfile({
          variables: {
            where: { id: editingChild?.id, userSubscriptionId },
            data: values,
          },
        })
      : await createChildProfile({
          variables: { data: { userSubscriptionId, ...values } },
        });
    if (response?.data) {
      closeModal();
      onChanged?.();
    }
  };

  const renderChildRow = (childProfile) => {
    const ageLabel = getChildAgeLabel(childProfile?.dateOfBirth);
    const genderLabel = getGenderLabel(childProfile?.gender);
    const canSetDefault = !childProfile?.isDefault && childProfile?.isActive;
    const bornLabel = childProfile?.dateOfBirth
      ? `Born ${formatDate(childProfile?.dateOfBirth, DATE_FORMATS?.REGULAR)}`
      : null;
    const details = [ageLabel, genderLabel, bornLabel]?.filter(Boolean);
    return (
      <div
        key={childProfile?.id}
        className={`child-row ${childProfile?.isDefault ? 'is-default' : ''} ${
          childProfile?.isActive ? '' : 'is-inactive'
        }`}
      >
        <Avatar size={40} className="child-avatar">
          {getInitials(childProfile)}
        </Avatar>
        <div className="child-row-info">
          <div className="child-row-name">
            <Text strong ellipsis={{ tooltip: getFullName(childProfile) }}>
              {getFullName(childProfile)}
            </Text>
            {!childProfile?.isActive && <Tag color="red">Inactive</Tag>}
          </div>
          <Text
            type={childProfile?.dateOfBirth ? 'secondary' : 'warning'}
            className="ultra-plan-caption"
          >
            {childProfile?.dateOfBirth
              ? details?.join(' · ')
              : [
                  genderLabel,
                  'Date of birth missing · needed for age-matched toys',
                ]
                  ?.filter(Boolean)
                  ?.join(' · ')}
          </Text>
        </div>
        <div className="child-row-actions">
          {childProfile?.isDefault && (
            <Tooltip title="Roadmap toys are picked for this child">
              <span className="child-default-badge">
                <StarFilled /> Default child
              </span>
            </Tooltip>
          )}
          {canSetDefault && (
            <Popconfirm
              title={`Make ${
                childProfile?.firstName || 'this child'
              } the default child?`}
              description="The roadmap stays as it is. Toys generated, added or changed from now on are picked for this child."
              okText="Yes, make default"
              cancelText="No"
              onConfirm={() => {
                switchDefaultChild(childProfile);
              }}
            >
              <Button
                type="text"
                size="small"
                className="child-set-default"
                icon={<StarOutlined />}
                loading={switchingChildId === childProfile?.id}
                disabled={!!switchingChildId}
              >
                Set as default
              </Button>
            </Popconfirm>
          )}
          <span className="child-row-actions-divider" />
          <Tooltip title="Edit child">
            <Button
              size="small"
              type="text"
              icon={<EditOutlined />}
              aria-label="Edit child"
              onClick={() => setModalState({ isOpen: true, childProfile })}
            />
          </Tooltip>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (hasError) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Child profiles couldn't be loaded."
        >
          <Button icon={<ReloadOutlined />} onClick={() => onRetry()}>
            Retry
          </Button>
        </Empty>
      );
    }
    if (!childProfiles?.length) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No child profiles yet. Add the child the toy expert spoke about to start a roadmap."
        >
          <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
            Add Child
          </Button>
        </Empty>
      );
    }
    return (
      <>
        {!hasDefaultChild && (
          <Alert
            className="mb-16"
            type="warning"
            showIcon
            message="No default child yet"
            description="The roadmap is planned for one default child. Use “Make Default” on the right child."
          />
        )}
        <div className="child-rows">{childProfiles?.map(renderChildRow)}</div>
      </>
    );
  };

  return (
    <Card
      className="child-profiles-card"
      title={`Children${
        childProfiles?.length ? ` (${childProfiles?.length})` : ''
      }`}
      loading={loading}
      extra={
        !!childProfiles?.length && (
          <Button
            size="small"
            className="card-extra-button"
            icon={<PlusOutlined />}
            onClick={openAddModal}
          >
            Add Child
          </Button>
        )
      }
    >
      {renderContent()}
      <ChildProfileModal
        isModalOpen={modalState?.isOpen}
        childProfile={modalState?.childProfile}
        hasDefaultChild={hasDefaultChild}
        loading={isCreating || isUpdating}
        onCancel={closeModal}
        onSubmit={handleSubmit}
      />
    </Card>
  );
};

export default ChildProfilesSection;
